import { LoadingButton } from '@mui/lab';
import { Box, Grid } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FORM_STATUS } from '../../model/formStatus';
import { InviteDetailResponse } from '../../model/invite/InviteDetailResponse';
import { inviteService } from '../../services/inviteService';
import AlmostDoneComponent from './AlmostDoneComponent';

const defaultState = {
  invitationCodeGuid: '',
  firstName: '',
  lastName: '',
  speciality: '',
  email: '',
  password: '',
  confirmPassword: '',
};

interface Props {
  data?: InviteDetailResponse;
}

export const RedeemInviteFormComponent: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation('auth');
  const [formData, setFormData] = useState(defaultState);
  const [status, setStatus] = useState(FORM_STATUS.IDLE);
  const [touched, setTouched] = useState(defaultState);
  const [isLoading, setIsLoading] = useState(false);
  const [showAlmostDone, setShowAlmostDone] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData((curFormData) => {
        return {
          ...curFormData,
          invitationCodeGuid: data.invitationCodeGuid,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          speciality: data.speciality,
        };
      });
      getErrors(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  function handleChange(e: any) {
    e.persist();
    setFormData((curFormData) => {
      return {
        ...curFormData,
        [e.target.id]: e.target.value,
      };
    });
  }

  function handleBlur(e: any) {
    e.persist();
    setTouched((cur: any) => {
      return { ...cur, [e.target.id]: true };
    });
  }

  function getErrors(formData: any) {
    const result = {
      invitationCodeGuid: '',
      firstName: '',
      lastName: '',
      speciality: '',
      email: '',
      password: '',
      confirmPassword: '',
    };
    if (status === FORM_STATUS.COMPLETED) return result;

    if (!formData.invitationCodeGuid) {
      result.invitationCodeGuid = t('validation.inviteRequired');
    } else if (
      !/{?\w{8}-?\w{4}-?\w{4}-?\w{4}-?\w{12}}?/i.test(
        formData.invitationCodeGuid
      )
    ) {
      result.invitationCodeGuid = t('validation.inviteInvalid');
    }

    if (!formData.firstName) result.firstName = t('validation.firstNameRequired');
    if (!formData.lastName) result.lastName = t('validation.lastNameRequired');
    if (!formData.speciality)
      result.speciality = t('validation.specialityRequired');

    if (!formData.email) {
      result.email = t('validation.emailRequired');
    } else if (!/^.+@.+$/i.test(formData.email)) {
      result.email = t('validation.emailInvalid');
    }

    if (!formData.password) {
      result.password = t('validation.passwordRequired');
    }

    if (!formData.confirmPassword)
      result.confirmPassword = t('validation.confirmRequired');

    if (formData.password !== formData.confirmPassword) {
      result.password = t('validation.passwordMismatch');
      result.confirmPassword = t('validation.confirmMismatch');
    }

    if (
      !/^(?=^.{8,}$)(?=.*\d)(?=.*[!@#$%^&*]+)(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/.test(
        formData.password
      )
    ) {
      result.password = t('validation.passwordComplexity');
    }

    return result;
  }

  //Derived state
  const errors = getErrors(formData);
  const isValid = Object.values(errors).every((x) => x === null || x === '');

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus(FORM_STATUS.SUBMITTING);
    if (isValid) {
      setIsLoading(true);
      try {
        inviteService
          .redeemInvite(
            formData.invitationCodeGuid,
            formData.firstName,
            formData.lastName,
            formData.email,
            formData.speciality,
            formData.password,
            formData.confirmPassword
          )

          .then((response: any) => {
            setStatus(FORM_STATUS.COMPLETED);
            setFormData(defaultState);
            setIsLoading(false);
            setShowAlmostDone(true);
          });
      } catch (e) {
        //This is not the exception you are looking for
        setIsLoading(false);
      }
    } else {
      setStatus(FORM_STATUS.SUBMITTED);
    }
  };

  return (
    <>
      {showAlmostDone ? (
        <AlmostDoneComponent />
      ) : (
        <>
          <Box component="form" sx={{ mt: 1 }} onSubmit={onSubmit}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  autoComplete="invite-code"
                  name="invitationCodeGuid"
                  required
                  fullWidth
                  id="invitationCodeGuid"
                  label={t('invite.codeLabel')}
                  value={formData.invitationCodeGuid}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={status === FORM_STATUS.COMPLETED ? true : false}
                  helperText={
                    (touched.invitationCodeGuid ||
                      status === FORM_STATUS.SUBMITTED) &&
                    errors.invitationCodeGuid
                  }
                  error={
                    (touched.invitationCodeGuid !== '' ||
                      status === FORM_STATUS.SUBMITTED) &&
                    errors.invitationCodeGuid !== ''
                  }
                />
              </Grid>

              <Grid size={6}>
                <TextField
                  autoComplete="given-name"
                  name="firstName"
                  required
                  fullWidth
                  id="firstName"
                  label={t('invite.firstNameLabel')}
                  value={formData.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={status === FORM_STATUS.COMPLETED ? true : false}
                  helperText={
                    (touched.firstName || status === FORM_STATUS.SUBMITTED) &&
                    errors.firstName
                  }
                  error={
                    (touched.firstName !== '' ||
                      status === FORM_STATUS.SUBMITTED) &&
                    errors.firstName !== ''
                  }
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  required
                  fullWidth
                  id="lastName"
                  label={t('invite.lastNameLabel')}
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={status === FORM_STATUS.COMPLETED ? true : false}
                  helperText={
                    (touched.lastName || status === FORM_STATUS.SUBMITTED) &&
                    errors.lastName
                  }
                  error={
                    (touched.lastName !== '' ||
                      status === FORM_STATUS.SUBMITTED) &&
                    errors.lastName !== ''
                  }
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  autoComplete="speciality"
                  name="speciality"
                  required
                  fullWidth
                  id="speciality"
                  label={t('invite.specialityLabel')}
                  value={formData.speciality}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={status === FORM_STATUS.COMPLETED ? true : false}
                  helperText={
                    (touched.speciality || status === FORM_STATUS.SUBMITTED) &&
                    errors.speciality
                  }
                  error={
                    (touched.speciality !== '' ||
                      status === FORM_STATUS.SUBMITTED) &&
                    errors.speciality !== ''
                  }
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label={t('invite.emailLabel')}
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={status === FORM_STATUS.COMPLETED ? true : false}
                  helperText={
                    (touched.email || status === FORM_STATUS.SUBMITTED) &&
                    errors.email
                  }
                  error={
                    (touched.email !== '' ||
                      status === FORM_STATUS.SUBMITTED) &&
                    errors.email !== ''
                  }
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label={t('invite.passwordLabel')}
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={status === FORM_STATUS.COMPLETED ? true : false}
                  helperText={
                    (touched.password || status === FORM_STATUS.SUBMITTED) &&
                    errors.password
                  }
                  error={
                    (touched.password !== '' ||
                      status === FORM_STATUS.SUBMITTED) &&
                    errors.password !== ''
                  }
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  required
                  fullWidth
                  name="confirmPassword"
                  label={t('invite.confirmLabel')}
                  type="password"
                  id="confirmPassword"
                  autoComplete="confirm-new-password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={status === FORM_STATUS.COMPLETED ? true : false}
                  helperText={
                    (touched.confirmPassword ||
                      status === FORM_STATUS.SUBMITTED) &&
                    errors.confirmPassword
                  }
                  error={
                    (touched.confirmPassword !== '' ||
                      status === FORM_STATUS.SUBMITTED) &&
                    errors.confirmPassword !== ''
                  }
                />
              </Grid>
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Checkbox required name="acceptTerms" color="primary" />
                  }
                  label={t('invite.acceptTerms')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={status === FORM_STATUS.COMPLETED ? true : false}
                />
              </Grid>
            </Grid>

            <LoadingButton
              type="submit"
              loading={isLoading}
              variant="contained"
              color="primary"
              //className={classes.submit}
              sx={{ mt: 3 }}
              disabled={status === FORM_STATUS.COMPLETED ? true : false}
            >
              {t('invite.redeem')}
            </LoadingButton>
          </Box>
        </>
      )}
    </>
  );
};

export default RedeemInviteFormComponent;
